import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/common/Button/Button';
import { Badge } from '@/components/common/Badge/Badge';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { ModeCard } from '@/components/positions/ModeCard/ModeCard';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';

import styles from '@/styles/positions.module.css';

const modeOptions = [
  {
    mode: 'quick' as const,
    title: 'Rápida',
    description: '8 perguntas · 2 min',
  },
  {
    mode: 'complete' as const,
    title: 'Completa',
    description: '24 perguntas · 6 min',
  },
] as const;

export function PositionsStartPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, selectMode, status, error } = usePoliticalMatchFlow();
  const isLegacyRoute = location.pathname.startsWith('/posicoes');

  useSeo({
    title: buildPageTitle('Match Político'),
    description: 'Compare suas posições com as dos candidatos e descubra o Match Político em modo rápido ou completo.',
    canonicalPath: '/match-politico',
    indexable: !isLegacyRoute,
  });

  if (status === 'ready' && state.phase === 'summary') {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  return (
    <section className={styles.questionCard}>
      <PageBreadcrumb items={[{ label: 'Início', to: '/' }, { label: 'Match Político' }]} />

      <StepHeader
        kicker="Etapa 1"
        title="Match Político"
        description="Compare suas posições com as posições dos candidatos."
        actions={
          <>
            <Badge tone="neutral">Suas respostas ficam neste dispositivo.</Badge>
            <Button to="/match-politico/metodologia" variant="ghost" size="sm">
              Como funciona?
            </Button>
          </>
        }
      />

      <div className={styles.modeGrid}>
        {modeOptions.map((option) => (
          <ModeCard
            key={option.mode}
            mode={option.mode}
            title={option.title}
            description={option.description}
            selected={state.mode === option.mode}
            onSelect={selectMode}
          />
        ))}
      </div>

      <div className={styles.questionActions}>
        <div className={styles.questionActionsGroup}>
          <Button type="button" variant="secondary" onClick={() => navigate('/')}>Voltar</Button>
        </div>

        <Button type="button" disabled={!state.mode} onClick={() => navigate('/match-politico/perguntas')}>
          Continuar
        </Button>
      </div>

      {status === 'error' ? <p className={styles.flowError}>{error}</p> : null}
    </section>
  );
}
